import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState, ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogContent,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { CheckedState } from "@radix-ui/react-checkbox";

interface ConfirmationDialogProps {
  visible: boolean;
  title: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  redConfirm?: boolean;
  withCancel?: boolean;
  withCheckbox?: boolean;
  checkboxLabel?: string;
  confirmDisabled?: boolean;
}

const ConfirmationDialog = ({
  visible,
  title,
  message,
  confirmText = "Delete",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  isLoading = false,
  redConfirm = true,
  withCancel = true,
  withCheckbox = false,
  checkboxLabel = "I understand and confirm this action",
  confirmDisabled = false,
}: ConfirmationDialogProps) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckedChange = useCallback((checked: CheckedState) => {
    setIsChecked(checked === true);
  }, []);

  // Reset checkbox state when dialog visibility changes
  useEffect(() => {
    if (!visible) {
      setIsChecked(false);
    }
  }, [visible]);

  return (
    <Dialog open={visible} onOpenChange={onCancel}>
      <DialogContent className="w-[90%] max-w-sm rounded-lg bg-white p-6">
        <DialogHeader className="mb-2">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          {withCheckbox && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="confirmation-checkbox"
                checked={isChecked}
                onCheckedChange={handleCheckedChange}
              />
              <Label
                htmlFor="confirmation-checkbox"
                className="text-sm text-gray-700"
              >
                {checkboxLabel}
              </Label>
            </div>
          )}

          <div className="flex flex-row justify-end space-x-3">
            {withCancel && (
              <Button
                variant="secondary"
                onClick={onCancel}
                className="rounded-md px-4 py-2 hover:bg-gray-200"
              >
                <p className="font-medium">{cancelText}</p>
              </Button>
            )}
            <Button
              onClick={onConfirm}
              variant={redConfirm ? "destructive" : "default"}
              className={`rounded-md px-4 py-2 ${withCheckbox && !isChecked ? "opacity-50" : "opacity-100"}`}
              disabled={
                isLoading || (withCheckbox && !isChecked) || confirmDisabled
              }
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <p className="font-medium text-white">{confirmText}</p>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationDialog;
